<template>
    <!-- `v-text` rather than an interpolation: the pieces butt up against each
         other, and a template that puts them on separate lines would leave a
         space inside every one of them. -->
    <p class="rich" dir="auto">
        <component
            :is="piece.tag"
            v-for="(piece, index) in pieces"
            :key="index"
            :class="piece.class"
            :dir="piece.dir"
            :href="piece.href"
            :target="piece.href ? '_blank' : null"
            :rel="piece.href ? 'noopener noreferrer' : null"
            v-text="piece.value"
        />
    </p>
</template>

<script>
import { entityHref, readSegments } from '../entities'

export default {
    name: 'AssistantRichText',
    props: {
        // What the assistant said, with no marks in it. Used whole whenever
        // there is nothing to link — which is most replies.
        text: {
            type: String,
            default: '',
        },
        marked: {
            type: String,
            default: '',
        },
        entities: {
            type: Object,
            default: null,
        },
    },
    computed: {
        pieces: function () {
            const segments = readSegments(this.marked, this.entities)

            if (!segments.length) {
                return [{ tag: 'span', value: this.text, href: null }]
            }

            return segments.map((segment) =>
                segment.entity
                    ? this.readEntity(segment.entity)
                    : { tag: 'span', value: segment.text, href: null }
            )
        },
    },
    methods: {
        readEntity: function (entity) {
            const href = entityHref(entity)

            return {
                tag: href ? 'a' : 'span',
                value: entity.label,
                href,
                // A label is an identifier, not prose: `auto` isolates it so a
                // Hebrew sentence cannot reorder `№27684`, and an English one
                // cannot reorder a Hebrew name.
                dir: 'auto',
                // Deleted, or a kind of record this app has no screen for:
                // still named, because the assistant named it, but dimmed and
                // leading nowhere.
                class: href ? 'rich__entity' : 'rich__entity rich__entity--off',
            }
        },
    },
}
</script>

<style scoped lang="scss">
.rich {
    margin: 0;
    // `dir="auto"` decides once for the whole element. A reply is routinely
    // several lines with English terms inside Hebrew ones, so `plaintext`
    // resolves the direction per line instead.
    unicode-bidi: plaintext;
}

// Only ever drawn inside the assistant's own bubble, which is why the fill is
// white at low alpha: it lifts off the purple whatever the line around it says.
.rich__entity {
    padding: 1px 4px;
    border-radius: 5px;
    background: rgba(255, 255, 255, 0.18);
    color: inherit;
    font-weight: 600;
    text-decoration: none;
    // A chip that wraps across two lines keeps its corners on both halves.
    -webkit-box-decoration-break: clone;
    box-decoration-break: clone;
    transition: background 150ms ease;
}

a.rich__entity:hover,
a.rich__entity:focus-visible {
    background: rgba(255, 255, 255, 0.32);
}

.rich__entity--off {
    opacity: 0.65;
    text-decoration: line-through;
}

@media (prefers-reduced-motion: reduce) {
    .rich__entity {
        transition: none;
    }
}
</style>
